import { observer } from 'mobx-react';
import { Link, useLocation } from '@compat/router';
import useOutsideAlerter from '@customHooks/useOutsideAlerter';
import type { FC, SyntheticEvent } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Picture } from '@ui';
import {
  CatalogMenuItemType,
  type CatalogMenuItem
} from '@store/CatalogStore.type';
import icons from '@icons';
import { twcls } from '@utils/twcls';
import { WeeklyPromos } from '@components/LandingBlocks/OysterBarTest';
import useWindowSize from '@customHooks/useWindowSize';
import MobileCatalogView from '@routes/Catalog/components/MobileCatalogView';

const {
  chevronRight: { lightGrey: crLGOutlined },
  percent: { green: percentGreenOutlined },
  chevronLeft: { red: clRedOutlined }
} = icons.Outline;

const {
  fire: { salmon: fireSalmonFilled },
  flash: { red: flashRedFilled },
  crab: { red: crabRedFilled },
  pricetag: { midYellow: ptMYFilled }
} = icons.Fill;
interface CatalogBtnMobileListProps {
  hide: () => void;
  list: CatalogMenuItem[];
  isShow: boolean;
}

const CatalogBtnMobileList: FC<CatalogBtnMobileListProps> = observer(
  ({ hide, list, isShow }) => {
    const { isMobile } = useWindowSize();
    const location = useLocation();
    const footerLinks = [
      { title: 'Доставка и оплата', to: '/delivery' },
      { title: 'Устричные бары', to: '/ustrichnye-bary-v-moskve' },
      { title: 'Отзывы', to: '/reviews' },
      { title: 'Рецепты', to: '/cook' },
      { title: 'Оплата', to: '/payment' },
      { title: 'Бизнесу', to: '/opt' },
      { title: 'О нас', to: '/about' },
      { title: 'Легкий возврат', to: '/warranty' },
      { title: 'Гарантии', to: '/warranty' },
      { title: 'Контакты', to: '/contacts' }
    ];

    const barMenuCatalogItem = list.find(item => item.isBarMenu);

    const headerLinks: CatalogMenuItem[] = useMemo(
      () =>
        [
          barMenuCatalogItem,
          {
            title: 'Новинки',
            identify: 'newbee',
            url: '/catalog/newbee',
            icon: flashRedFilled,
            childrenItems: null,
            id: 'newbee',
            type: CatalogMenuItemType.Product,
            isActive: location.pathname === '/catalog/newbee',
            isBarMenu: false
          },
          {
            title: 'Скидки',
            identify: 'promo',
            url: '/catalog/promo',
            icon: fireSalmonFilled,
            childrenItems: null,
            id: 'promo',
            type: CatalogMenuItemType.Product,
            isActive: location.pathname === '/catalog/promo',
            isBarMenu: false
          },
          {
            title: 'Акции',
            identify: 'promotions',
            icon: percentGreenOutlined,
            url: '/promotions',
            childrenItems: null,
            id: 'promotions',
            type: CatalogMenuItemType.Product,
            isActive: location.pathname === '/promotions',
            isBarMenu: false
          },
          {
            title: 'Распродажа',
            identify: 'rasprodazha',
            url: '/catalog/rasprodazha',
            icon: ptMYFilled,
            childrenItems: null,
            id: 'rasprodazha',
            type: CatalogMenuItemType.Product,
            isActive: location.pathname === '/catalog/rasprodazha',
            isBarMenu: false
          }
        ].filter((item): item is CatalogMenuItem => Boolean(item)),
      [barMenuCatalogItem, location.pathname]
    );
    const [subMenu, setSubMenu] = useState<CatalogMenuItem | null>(null);
    useEffect(() => {
      if (!isShow) {
        setSubMenu(null);
      }
    }, [isShow]);
    const ref = useRef<HTMLDivElement>(null);
    const allowOutsideCloseRef = useRef(false);
    useEffect(() => {
      if (!isShow) {
        allowOutsideCloseRef.current = false;
        return;
      }
      const timeoutId = window.setTimeout(() => {
        allowOutsideCloseRef.current = true;
      }, 0);
      return () => {
        window.clearTimeout(timeoutId);
        allowOutsideCloseRef.current = false;
      };
    }, [isShow]);
    const handleOutside = useCallback(() => {
      if (!allowOutsideCloseRef.current) {
        return;
      }
      hide();
    }, [hide]);
    useOutsideAlerter({
      ref,
      callback: handleOutside,
      excludeSelector: '.mobile-bottom-nav'
    });

    const onItemClick = (e: SyntheticEvent): void => {
      e.stopPropagation();
      hide();
    };
    const memoizedList: CatalogMenuItem[] = useMemo(
      () => [...headerLinks, ...list.filter(item => !item.isBarMenu)],
      [list, headerLinks]
    );
    const [menuTop, setMenuTop] = useState(0);
    useLayoutEffect(() => {
      if (!isShow) {
        return;
      }
      const updateTop = (): void => {
        const bottom = document
          .querySelector('.header-main__main')
          ?.getBoundingClientRect().bottom;
        const nextTop = bottom ?? 0;
        setMenuTop(prev => (prev === nextTop ? prev : nextTop));
      };
      updateTop();
      window.addEventListener('scroll', updateTop, true);
      window.addEventListener('resize', updateTop);
      return () => {
        window.removeEventListener('scroll', updateTop, true);
        window.removeEventListener('resize', updateTop);
      };
    }, [isShow]);
    const closeSubMenu = (): void => {
      setSubMenu(null);
    };

    return (
      <div
        ref={ref}
        style={{
          top: menuTop
        }}
        className={twcls(
          ` w-full transition-opacity-transform duration-300 backface-hidden fixed left-0 bottom-0 sm:bottom-[54px] z-[1000000] flex opacity-0`,
          isMobile ? 'bg-[#f6f5f7]' : 'bg-white',
          isShow
            ? !isMobile && subMenu?.identify
              ? '-translate-x-full'
              : 'translate-x-0'
            : '-translate-x-[105%]',
          isShow && 'opacity-100'
        )}
      >
        {isMobile ? (
          <nav className='px-3 py-3 bg-[#f6f5f7] pb-0 overflow-auto w-screen min-w-[100vw] flex-1'>
            <MobileCatalogView isOpen={isShow} onNavigate={hide} />
          </nav>
        ) : (
          <>
        <nav
          className={twcls(
            `px-3  md:px-6.25  py-3 bg-white pb-0 overflow-auto w-screen  min-w-[100vw] flex-1`
          )}
        >
          <ul className='w-full mb-3 overflow-auto font-medium bg-white'>
            {memoizedList?.map(item => {
              if (item.id === '984') {
                return null;
              }
              if (item.isBarMenu) {
                return (
                  <li
                    key={item?.identify ?? item?.title}
                    className={twcls(
                      'border-0 border-solid border-[var(--light-grey)] bg-[#E0F4FF] border-b last-of-type:border-b-0 rounded-[10px]',
                      subMenu?.isHidden && 'hidden'
                    )}
                  >
                    <div
                      onClick={() => {
                        setSubMenu(prev => {
                          if (prev?.identify === item?.identify) return null;
                          return item;
                        });
                      }}
                      className='flex items-center justify-between '
                    >
                      <div
                        className={twcls(
                          'flex items-center w-full h-full py-1.5 gap-3 px-3 ',
                          item.isActive && 'text-[var(--dark-red)] bg-[#E0F4FF]'
                        )}
                      >
                        {item.icon && (
                          <Picture
                            icon={item.icon}
                            width={'44'}
                            height={'44'}
                          />
                        )}
                        {item.title}
                      </div>
                      {!!item.childrenItems?.length && (
                        <div
                          className={twcls(
                            `h-7
                        transition-transform
                        min-w-[1.75rem]
                        w-7
                        flex
                        items-center
                        justify-center
                        rounded
                        `,
                            subMenu?.identify === item?.identify &&
                              'text-[var(--dark-red)]'
                          )}
                        >
                          <Picture icon={crLGOutlined} height='24' width='24' />
                        </div>
                      )}
                    </div>
                  </li>
                );
              }
              return (
                <li
                  key={item?.identify ?? item?.title}
                  className='border-0 border-solid border-[var(--light-grey)] border-b last-of-type:border-b-0'
                >
                  <div className='flex items-center justify-between '>
                    <Link
                      className={twcls(
                        'flex items-center w-full h-full py-4',
                        item.isActive && 'text-[var(--dark-red)]'
                      )}
                      onClick={onItemClick}
                      to={item.url}
                    >
                      {item.title}
                      {item.icon && typeof item.icon === 'object' && (
                        <Picture
                          pictureClassName={'ml-1'}
                          icon={item.icon}
                          width={'16'}
                          height={'16'}
                        />
                      )}
                    </Link>
                    {!!item.childrenItems?.length && (
                      <div
                        className={twcls(
                          `h-7
                        transition-transform
                        min-w-[1.75rem]
                        w-7
                        flex
                        items-center
                        justify-center
                        rounded
                        bg-[var(--light-grey)]
                        `,
                          subMenu?.identify === item?.identify &&
                            'text-[var(--dark-red)]'
                        )}
                        onClick={() => {
                          setSubMenu(prev => {
                            if (prev?.identify === item?.identify) return null;
                            return item;
                          });
                        }}
                      >
                        <Picture icon={crLGOutlined} height='24' width='24' />
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className='bg-[var(--light-grey)] pt-3 pb-6 sm:px-3 md:px-6.25 sm:-mx-3 md:-mx-6.25 mt-auto'>
            <ul className='mb-2 font-medium'>
              {footerLinks?.map(item => (
                <li className='py-3' onClick={onItemClick} key={item.title}>
                  <Link to={item.to}> {item.title}</Link>
                </li>
              ))}
            </ul>
            <div className=''>
              <a className='mb-1 font-medium' href='tel:+74956409640'>
                +7 495 640 9 640
              </a>
              <div className='text-xs text-[var(--ultra-dark-grey)]'>
                С 06:00 до 00:00
              </div>
            </div>
          </div>
        </nav>
        <div
          className={twcls(
            'w-screen min-w-[100vw] duration-300 overflow-auto transition-visibility flex-1 h-full bg-white px-3 md:px-6.25 py-3 invisible',
            subMenu?.identify && ' visible'
          )}
        >
          {subMenu?.isBarMenu && (
            <div className='mb-8 '>
              <WeeklyPromos className='px-0' />
            </div>
          )}
          {subMenu !== null && !!subMenu?.childrenItems?.length && (
            <>
              <div
                className='flex items-center text-sm text-[var(--dark-red)] mb-3'
                onClick={closeSubMenu}
              >
                <Picture
                  pictureClassName={'mr-1'}
                  icon={clRedOutlined}
                  width={'16px'}
                  height={'16px'}
                />
                Назад
              </div>
              <ul
                className={twcls(
                  'px-3 font-normal w-full overflow-auto',
                  subMenu?.isBarMenu &&
                    'px-0 grid gap-2 grid-cols-[repeat(auto-fill,_minmax(151px,_1fr))]'
                )}
              >
                {subMenu.childrenItems.map(i => {
                  if (subMenu.isBarMenu) {
                    return (
                      <li
                        key={i?.identify || i?.title}
                        className={twcls(
                          'w-full aspect-square rounded-xl bg-[var(--light-grey)]'
                        )}
                      >
                        <Link
                          onClick={onItemClick}
                          to={i.url}
                          className='flex flex-col items-center justify-center w-full h-full p-1 text-sm text-center'
                        >
                          {i?.icon && (
                            <Picture
                              icon={i.icon}
                              width={104}
                              height={94}
                              className='mb-4'
                            />
                          )}
                          {i.title}
                        </Link>
                      </li>
                    );
                  }
                  return (
                    <li key={i?.identify || i?.title} className='w-full'>
                      <Link
                        onClick={onItemClick}
                        to={i.url}
                        className='flex items-center w-full py-4'
                      >
                        {i.title}
                        {i.identify === 'newbee' && (
                          <Picture
                            pictureClassName={'ml-1.5'}
                            icon={flashRedFilled}
                            height='20'
                            width='20'
                          />
                        )}
                        {i.identify === 'delikateska' && (
                          <Picture
                            pictureClassName={'ml-1.5'}
                            icon={crabRedFilled}
                            height='20'
                            width='20'
                          />
                        )}
                        {i.identify === 'promo' && (
                          <Picture
                            pictureClassName={'ml-1.5'}
                            icon={ptMYFilled}
                            height='20'
                            width='20'
                          />
                        )}
                        {!!i?.rubticProductsCount &&
                          i.rubticProductsCount > 0 && (
                            <span className='ml-1.5 text-[#c2c2c2]'>
                              {i.rubticProductsCount}
                            </span>
                          )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
          </>
        )}
      </div>
    );
  }
);

export default CatalogBtnMobileList;
